import { Date, Link, PageWrapper, Search } from "@/components";
import { ViperRangePicker } from "@/components/ViperDatePickers/ViperRangePicker";
import * as GlobalS from "@/components/styled";
import { CONTENT_WIDTH, GUTTER_SIZE } from "@/enums";
import { routes, selectFilterHelper } from "@/helpers";
import { useProjectContext } from "@/providers";
import { useGetProjectHistoryQuery } from "@/services";
import { Pager } from "@/sharedStyled";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Row, Select, Table } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrayParam,
  NumberParam,
  StringParam,
  useQueryParams,
} from "use-query-params";

const PAGINATION_PAGE_SIZE = 10;
const DEFAULT_PAGE = 1;

export const ProjectHistory = () => {
  const { projectId } = useParams();
  const intl = useIntl();
  const { project } = useProjectContext();

  const [queryParams, setQueryParams] = useQueryParams({
    page: NumberParam,
    orderBy: ArrayParam,
    query: StringParam,
    entityType: StringParam,
    fromDate: StringParam,
    toDate: StringParam,
  });
  const [queryRequest, setQueryRequest] = useState();
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  const locale = useSelector((state) => state.locales.locale);

  const {
    data: { data, totalCount } = {},
    isFetching,
    isError,
  } = useGetProjectHistoryQuery(
    { projectId, queryParameters: queryRequest },
    { skip: !queryRequest }
  );

  useEffect(() => {
    const queryRequest = {
      orderBy: ["CreatedAt desc"],
      pageSize: PAGINATION_PAGE_SIZE,
      languageCode: locale,
      ...queryParams,
    };
    if (queryRequest.page <= 0 || !queryRequest.page) queryRequest.page = 1;
    if (queryRequest.pageSize <= 0 || !queryRequest.pageSize)
      queryRequest.pageSize = PAGINATION_PAGE_SIZE;

    setQueryRequest(queryRequest);
  }, [locale, queryParams]);

  useEffect(() => {
    if (!isError) return;

    toast.error(intl.formatMessage({ id: "general.toastSomethingWentWrong" }));
  }, [intl, isError]);

  const columns = [
    {
      title: intl.formatMessage({ id: "projectHistory.columns.createdAt" }),
      dataIndex: "createdAt",
      render: (data) => <Date value={data} showTime />,
      width: 155,
    },
    {
      title: intl.formatMessage({
        id: "projectHistory.columns.createdByDisplayName",
      }),
      dataIndex: "createdByDisplayName",
      width: 155,
      ellipsis: true,
    },
    {
      title: intl.formatMessage({
        id: "projectHistory.columns.relatedEntityDescription",
      }),
      dataIndex: "relatedEntityDescription",
      width: "50%",
    },
    {
      title: intl.formatMessage({
        id: "projectHistory.columns.changeDescription",
      }),
      dataIndex: "changeDescription",
      width: "40%",
    },
    {
      title: intl.formatMessage({ id: "projectHistory.columns.entityType" }),
      dataIndex: "entityType",
      render: (data) =>
        intl.formatMessage({
          id: `projectHistory.entityTypes.${data}`,
          defaultMessage: data,
        }),
      width: 150,
    },
  ];

  const clearAll = useCallback(() => {
    setQueryParams(
      { page: DEFAULT_PAGE, pageSize: PAGINATION_PAGE_SIZE },
      "push"
    );
  }, [setQueryParams]);

  const onDateChanged = (value) => {
    setQueryParams({
      fromDate: value[0]?.format("YYYY-MM-DD"),
      toDate: value[1]?.format("YYYY-MM-DD"),
      page: DEFAULT_PAGE,
    });
  };

  const getDateValues = useCallback(() => {
    const values = [];
    if (queryParams.fromDate) {
      values.push(dayjs(queryParams.fromDate));
    } else {
      values.push(undefined);
    }
    if (queryParams.toDate) {
      values.push(dayjs(queryParams.toDate));
    } else {
      values.push(undefined);
    }
    return values;
  }, [queryParams.fromDate, queryParams.toDate]);

  return (
    <PageWrapper
      pageTitle={intl.formatMessage({ id: "projectHistory.title" })}
      backButtonRoute={routes.projects.get(projectId)}
    >
      <Row className="pb-2">
        <Col
          xxl={CONTENT_WIDTH.oneThird}
          lg={CONTENT_WIDTH.half}
          md={CONTENT_WIDTH.twoThirds}
          xxs={CONTENT_WIDTH.max}
        >
          <Search
            queryParams={queryParams}
            setQueryParams={setQueryParams}
            loadingSearchResults={isFetching}
            clearAll={clearAll}
            searchTooltip={intl.formatMessage({
              id: "projectHistory.searchTooltip",
            })}
            searchPlaceholder={intl.formatMessage({
              id: "general.searchPlaceholder",
            })}
            parentComponentName="project-history-list"
          />
        </Col>
        <Col className="pl-1">
          <Button
            data-test-id="project-history-search-advanced-btn"
            icon={
              <FontAwesomeIcon
                icon={isAdvancedOpen ? faChevronUp : faChevronDown}
              />
            }
            onClick={() => setIsAdvancedOpen((previousState) => !previousState)}
          >
            {intl.formatMessage({ id: "general.advanced" })}
          </Button>
        </Col>
      </Row>
      {isAdvancedOpen ? (
        <Row className="pb-2" gutter={GUTTER_SIZE.large}>
          <Col>
            <ViperRangePicker
              id={"project-history-search-date-range"}
              data-test-id={"project-history-viper-range-picker"}
              value={getDateValues()}
              onChange={onDateChanged}
            />
          </Col>
          <Col>
            <Select
              data-test-id={"project-history-search-entity-type"}
              value={queryParams.entityType}
              onChange={(value) =>
                setQueryParams({ entityType: value, page: DEFAULT_PAGE })
              }
              style={{ width: 300 }}
              allowClear
              placeholder={intl.formatMessage({
                id: "projectHistory.entityType",
              })}
              filterOption={selectFilterHelper}
            >
              <Select.Option value={"Project"}>
                {intl.formatMessage({
                  id: "projectHistory.entityTypes.Project",
                })}
              </Select.Option>
              <Select.Option value={"ProjectMilestoneSchedule"}>
                {intl.formatMessage({
                  id: "projectHistory.entityTypes.ProjectMilestoneSchedule",
                })}
              </Select.Option>
              <Select.Option value={"ProjectMilestone"}>
                {intl.formatMessage({
                  id: "projectHistory.entityTypes.ProjectMilestone",
                })}
              </Select.Option>
              <Select.Option value={"ProjectMilestoneTask"}>
                {intl.formatMessage({
                  id: "projectHistory.entityTypes.ProjectMilestoneTask",
                })}
              </Select.Option>
              <Select.Option value={"ProjectMilestoneRole"}>
                {intl.formatMessage({
                  id: "projectHistory.entityTypes.ProjectMilestoneRole",
                })}
              </Select.Option>
              <Select.Option value={"ProjectRole"}>
                {intl.formatMessage({
                  id: "projectHistory.entityTypes.ProjectRole",
                })}
              </Select.Option>
              <Select.Option value={"RegionSpecificProjectRole"}>
                {intl.formatMessage({
                  id: "projectHistory.entityTypes.RegionSpecificProjectRole",
                })}
              </Select.Option>
              <Select.Option value={"ProjectTeamAssignment"}>
                {intl.formatMessage({
                  id: "projectHistory.entityTypes.ProjectTeamAssignment",
                })}
              </Select.Option>
              <Select.Option value={"StockKeepingUnit"}>
                {intl.formatMessage({
                  id: "projectHistory.entityTypes.StockKeepingUnit",
                })}
              </Select.Option>
              <Select.Option value={"WorkflowHistory"}>
                {intl.formatMessage({
                  id: "projectHistory.entityTypes.WorkflowHistory",
                })}
              </Select.Option>
              <Select.Option value={"ProjectMilestoneNotification"}>
                {intl.formatMessage({
                  id: "projectHistory.entityTypes.ProjectMilestoneNotification",
                })}
              </Select.Option>
            </Select>
          </Col>
        </Row>
      ) : undefined}
      <Row gutter={GUTTER_SIZE.large}>
        <Col span={CONTENT_WIDTH.max}>
          <GlobalS.PageContentCard
            title={"History"}
            extra={
              project.isMigrated && (
                <Button
                  data-test-id="view-vpin-project-history-btn"
                  type="primary"
                >
                  <Link to={routes.projects.vpinHistory(projectId)}>
                    {intl.formatMessage({
                      id: "projectHistory.viewVpinHistory",
                    })}
                  </Link>
                </Button>
              )
            }
          >
            <Table
              data-test-id="project-history-table"
              columns={columns}
              dataSource={data}
              loading={isFetching}
              rowKey={({ id }) => id}
              pagination={false}
              scroll={{ x: 1200 }}
            />
            <Pager
              defaultCurrent={queryParams.page || DEFAULT_PAGE}
              current={queryParams.page || DEFAULT_PAGE}
              pageSize={PAGINATION_PAGE_SIZE}
              total={totalCount}
              showSizeChanger={false}
              onChange={(page) => setQueryParams({ page })}
              data-test-id="projects--history-table-pagination"
            />
          </GlobalS.PageContentCard>
        </Col>
      </Row>
    </PageWrapper>
  );
};
